"use client";

import {
  ArrowRight,
  Check,
  CheckCircle,
  CircleNotch,
  Eye,
  EyeSlash,
  GoogleLogo,
  Moon,
  SealCheck,
  ShieldCheck,
  Sun,
  Trophy,
  UsersThree,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { FormEvent, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import {
  ApiError,
  changeEmailVerificationAddress,
  EmailVerificationDelivery,
  googleLoginUrl,
  login,
  logout as logoutSession,
  restoreSession,
  requestEmailVerificationCode,
  signup,
  requestPasswordReset,
  verifyEmailCode,
} from "../lib/api";
import styles from "./kaizen-app.module.css";
import { PublicAnalyticsPage } from "./analytics-consent";
import { SiteFooter, SiteHeader, SkipLink } from "./site-chrome";
import { HOME_AUDIENCES, HOME_FAQ } from "../lib/home-content";
import { SITE_TITLE } from "../lib/site";
import { SUPPORT_EMAIL } from "../lib/contact";
import { Brand } from "./app-chrome";

const ParticleWalker = dynamic(() => import("./particle-walker"), { ssr: false });
// The signed-in workspace is most of the app; visitors to the landing page
// never download it.
const ProductionApplication = dynamic(
  () => import("./workspace").then((module) => module.ProductionApplication),
  { ssr: false, loading: () => <div className={styles.appLoading} aria-busy="true" /> },
);
const DemoApplication = dynamic(
  () => import("./workspace").then((module) => module.DemoApplication),
  { ssr: false },
);

type Screen = "landing" | "auth" | "app" | "verification";

type AuthMode = "new" | "back";

type PendingEmailVerification = EmailVerificationDelivery & {
  expiresAt: number;
  resendAvailableAt: number;
};

const oauthErrorMessages: Record<string, string> = {
  account_disabled: `This account is disabled. Contact ${SUPPORT_EMAIL} for help.`,
  google_not_configured:
    "Google sign-in still needs a client ID and client secret in the backend environment.",
  google_cancelled: "Google sign-in was cancelled. No changes were made.",
  google_state_invalid:
    "That Google sign-in attempt expired or could not be verified. Please try again.",
  google_failed:
    "Google could not securely complete sign-in. Please try again in a moment.",
  google_account_conflict:
    "This email is already connected to a different Google identity. Sign in another way and review your account settings.",
};

const pendingVerificationKey = "kaizen:pending-email-verification";

function pendingVerification(
  delivery: EmailVerificationDelivery,
): PendingEmailVerification {
  const now = Date.now();
  return {
    ...delivery,
    expiresAt: now + delivery.expires_in * 1000,
    resendAvailableAt: now + delivery.resend_available_in * 1000,
  };
}

function readPendingVerification(): PendingEmailVerification | null {
  try {
    const value = window.localStorage.getItem(pendingVerificationKey);
    if (!value) return null;
    const pending = JSON.parse(value) as PendingEmailVerification;
    if (
      typeof pending.verification_flow_id !== "string" ||
      typeof pending.email !== "string" ||
      typeof pending.expiresAt !== "number" ||
      typeof pending.resendAvailableAt !== "number"
    ) {
      return null;
    }
    return {
      ...pending,
      purpose: pending.purpose === "sign_in" ? "sign_in" : "verify_email",
    };
  } catch {
    return null;
  }
}

function writePendingVerification(
  pending: PendingEmailVerification | null,
): void {
  try {
    if (pending) {
      window.localStorage.setItem(
        pendingVerificationKey,
        JSON.stringify(pending),
      );
    } else {
      window.localStorage.removeItem(pendingVerificationKey);
    }
  } catch {
    // The in-memory flow remains usable when browser storage is unavailable.
  }
}

type NetworkInformation = { saveData?: boolean; effectiveType?: string };

/** Data-saver, very slow connections and low-memory phones skip the 3D figure. */
function prefersLightweightHero() {
  const nav = navigator as Navigator & { connection?: NetworkInformation; deviceMemory?: number };
  return Boolean(
    nav.connection?.saveData
    || ["slow-2g", "2g"].includes(nav.connection?.effectiveType ?? "")
    || (nav.deviceMemory !== undefined && nav.deviceMemory <= 2),
  );
}

function WalkingFigure() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (prefersLightweightHero()) return;
    // Fetch three.js and the model only once the page has settled.
    const idle = window.requestIdleCallback ?? ((callback: () => void) => window.setTimeout(callback, 1));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const handle = idle(() => setShow(true), { timeout: 2000 });
    return () => cancel(handle);
  }, []);
  return show ? <ParticleWalker className={styles.particleWalker} /> : null;
}

function Landing({ onAuth, ready = true }: { onAuth: (mode: AuthMode) => void; ready?: boolean }) {
  return (
    <div className={styles.landing}>
      {ready && <PublicAnalyticsPage path="/" title={SITE_TITLE} />}
      <SkipLink />
      <SiteHeader variant="dark" />

      <main id="main-content">
        <section className={styles.hero}>
          <div className={styles.heroEyebrow}>
            <span>GOAL TRACKING &amp; GROUP ACCOUNTABILITY</span>
            <span>01 — 04</span>
          </div>
          <h1 className={styles.heroTitle}>
            {ready && <WalkingFigure />}
            <span>SHOW</span>{" "}
            <span className={styles.outlineWord}>THE WORK.</span>
          </h1>
          <div className={styles.heroBottom}>
            <div className={styles.proofOrbit} aria-hidden="true">
              <div className={styles.orbitLine} />
              <div className={styles.orbitCardOne}>
                <Check size={15} /> VERIFIED
              </div>
              <div className={styles.orbitCardTwo}>
                <span>27</span>
                DAY STREAK
              </div>
            </div>
            <div className={styles.heroCopy}>
              <p>
                Kaizen is a goal-tracking and accountability app for small private
                groups. Declare what matters in the morning, attach evidence by noon
                the next day, and let the people you trust verify the work.
              </p>
              <div className={styles.heroActions}>
                <button
                  className={styles.primaryLight}
                  onClick={() => onAuth("new")}
                >
                  I&apos;m new here <ArrowRight size={17} />
                </button>
                <button
                  className={styles.ghostDark}
                  onClick={() => onAuth("back")}
                >
                  I&apos;m back
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.statement} id="method">
          <p className={styles.kicker}>THE KAIZEN METHOD</p>
          <h2>
            Ambition becomes visible when it leaves a trail of
            <span> commitments, evidence, and honest review.</span>
          </h2>
          <div className={styles.methodGrid}>
            {[
              [
                "01",
                "Declare",
                "Set a measurable outcome before the day begins.",
              ],
              [
                "02",
                "Do the work",
                "Keep the top three commitments unmistakably clear.",
              ],
              [
                "03",
                "Bring proof",
                "Close every commitment with evidence and reflection.",
              ],
              [
                "04",
                "Get verified",
                "Your accountability group confirms the outcome.",
              ],
            ].map(([number, title, copy]) => (
              <article key={number}>
                <span>{number}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.productShowcase} id="proof">
          <div className={styles.showcaseIntro}>
            <p className={styles.kicker}>ONE DAY, CLOSED PROPERLY</p>
            <h2>Proof over promises.</h2>
            <p>
              Every record keeps the original declaration, every evidence
              version, and every peer decision—so progress cannot be rewritten
              after the fact.
            </p>
          </div>
          <div className={styles.recordMockup}>
            <div className={styles.mockupTop}>
              <Brand compact />
              <span>DAILY RECORD / JUL 29</span>
              <span>VERIFIED</span>
            </div>
            <div className={styles.mockupBody}>
              <div className={styles.mockupNumber}>03</div>
              <div className={styles.mockupRecord}>
                <p>TOP COMMITMENT</p>
                <h3>Complete the authentication chapter</h3>
                <div className={styles.mockupMeta}>
                  <span>1,500 edited words</span>
                  <span>3 evidence items</span>
                  <span>Approved by Amina</span>
                </div>
              </div>
              <div className={styles.mockupSeal}>
                <SealCheck size={32} weight="thin" />
                PEER VERIFIED
              </div>
            </div>
          </div>
        </section>

        <section className={styles.groupSection} id="groups">
          <div>
            <p className={styles.kicker}>LOCK IN TOGETHER</p>
            <h2>Your work stays inside the room.</h2>
          </div>
          <div className={styles.groupFeatures}>
            <article>
              <ShieldCheck size={30} weight="thin" />
              <h3>Private by default</h3>
              <p>
                Only authenticated members of your accountability group can see
                its work.
              </p>
            </article>
            <article>
              <UsersThree size={30} weight="thin" />
              <h3>Up to fifteen</h3>
              <p>
                Small enough for trust, large enough for meaningful peer
                accountability.
              </p>
            </article>
            <article>
              <Trophy size={30} weight="thin" />
              <h3>Execution wins</h3>
              <p>
                Planning matters, but verified achievement carries sixty percent
                of the score.
              </p>
            </article>
          </div>
          <button className={styles.statementCta} onClick={() => onAuth("new")}>
            Start your accountability group <ArrowRight size={18} />
          </button>
        </section>

        <section className={styles.audienceSection} aria-labelledby="audience-heading">
          <p className={styles.kicker}>WHO IT&apos;S FOR</p>
          <h2 id="audience-heading">For people who keep promises to each other.</h2>
          <p className={styles.sectionLead}>
            Kaizen suits small groups who already meet, study or work together and
            want progress they can see. Goals become milestones, milestones become
            daily commitments, and every commitment ends with proof and a peer review.
          </p>
          <div className={styles.audienceGrid}>
            {HOME_AUDIENCES.map((audience) => (
              <article key={audience.title}>
                <h3>{audience.title}</h3>
                <p>{audience.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.faqSection} aria-labelledby="faq-heading">
          <p className={styles.kicker}>QUESTIONS</p>
          <h2 id="faq-heading">Before you start a group.</h2>
          <div className={styles.faqList}>
            {HOME_FAQ.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
          <p className={styles.sectionLead}>
            New to accountability groups? Read{" "}
            <Link href="/how-it-works">how Kaizen works</Link> or the{" "}
            <Link href="/guides/start-an-accountability-group">guide to starting a group</Link>.
          </p>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function AuthPanel({
  mode,
  onMode,
  authError,
  onGoogle,
  onClose,
  onEnter,
  onVerification,
  onVerificationRecovery,
}: {
  mode: AuthMode;
  onMode: (mode: AuthMode) => void;
  authError: string | null;
  onGoogle: (signupConfirmed: boolean) => void;
  onClose: () => void;
  onEnter: () => void;
  onVerification: (delivery: EmailVerificationDelivery) => void;
  onVerificationRecovery: (email?: string) => void;
}) {
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [agreementToast, setAgreementToast] = useState(0);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const submissionInProgress = useRef(false);

  useEffect(() => {
    if (!agreementToast) return;
    const timeout = window.setTimeout(() => setAgreementToast(0), 5000);
    return () => window.clearTimeout(timeout);
  }, [agreementToast]);

  const checkAgreement = () => {
    if (mode !== "new" || (policiesAccepted && ageConfirmed)) return true;
    setAgreementToast((current) => current + 1);
    return false;
  };

  const sendResetLink = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setFormError(null);
    setNotice(null);
    try {
      setNotice(await requestPasswordReset(String(new FormData(event.currentTarget).get("email") ?? "")));
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Kaizen could not send a reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setForgotPassword(false);
    setFormError(null);
    setNotice(null);
    setPasswordVisible(false);
    setPoliciesAccepted(false);
    setAgeConfirmed(false);
    setAgreementToast(0);
    onMode(mode === "new" ? "back" : "new");
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submissionInProgress.current || googleLoading) return;
    if (!checkAgreement()) return;

    submissionInProgress.current = true;
    setLoading(true);
    setFormError(null);
    setNotice(null);
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      if (mode === "new") {
        const delivery = await signup({
          policies_accepted: policiesAccepted,
          age_confirmed: ageConfirmed,
          name: String(form.get("name") ?? ""),
          email: String(form.get("email") ?? ""),
          password: String(form.get("password") ?? ""),
        });
        onVerification(delivery);
      } else {
        const email = String(form.get("email") ?? "");
        await login(
          email,
          String(form.get("password") ?? ""),
        );
        onEnter();
      }
    } catch (error) {
      if (
        mode === "back" &&
        error instanceof ApiError &&
        error.message === "Email is not verified"
      ) {
        onVerificationRecovery(
          String(new FormData(formElement).get("email") ?? ""),
        );
        return;
      }
      setNotice(null);
      setFormError(
        error instanceof ApiError
          ? error.message
          : "Kaizen could not complete authentication. Please try again.",
      );
    } finally {
      submissionInProgress.current = false;
      setLoading(false);
    }
  };

  return (
    <main
      className={`${styles.authScreen} ${
        theme === "dark" ? styles.authDark : styles.authLight
      }`}
    >
      <div className={styles.authEditorial}>
        <button className={styles.authBrandButton} onClick={onClose}>
          <Brand />
        </button>
        <div>
          <p className={styles.kicker}>CONSISTENCY, WITNESSED.</p>
          <h2>Turn intention into a record you can stand behind.</h2>
        </div>
        <div className={styles.authQuote}>
          <span>“</span>
          <p>
            The work becomes real when the proof remains after motivation
            leaves.
          </p>
        </div>
      </div>
      <div className={styles.authFormPanel}>
        <button
          className={styles.themeButton}
          type="button"
          onClick={() => setTheme(theme === "light" ? "dark" : "light")}
          aria-label={
            theme === "light" ? "Switch to dark mode" : "Switch to light mode"
          }
          title={
            theme === "light" ? "Switch to dark mode" : "Switch to light mode"
          }
        >
          {theme === "light" ? (
            <Moon size={24} weight="regular" />
          ) : (
            <Sun size={24} weight="regular" />
          )}
        </button>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close authentication"
        >
          <X size={20} />
        </button>
        <div className={styles.authFormInner}>
          <p className={styles.kicker}>
            {mode === "new" ? "CREATE YOUR ACCOUNT" : forgotPassword ? "PASSWORD RESET" : "WELCOME BACK"}
          </p>
          <h1>
            {mode === "new"
              ? "Start showing the work."
              : forgotPassword
                ? "Reset your password."
                : "Continue where you left off."}
          </h1>
          <p className={styles.authDescription}>
            {mode === "new"
              ? "Create a private account, then create a group or join one with an invitation code."
              : forgotPassword
                ? "We'll email a single-use link that works for one hour."
                : "Sign in securely. We never reveal whether an email belongs to an account."}
          </p>

          {notice ? (
            <div className={styles.authNotice} role="status">
              <CheckCircle size={19} />
              <span>{notice}</span>
            </div>
          ) : (authError || formError) ? (
            <div className={styles.authError} role="alert">
              <WarningCircle size={19} />
              <span>{authError || formError}</span>
            </div>
          ) : null}

          {forgotPassword ? (
            <form className={styles.authForm} onSubmit={sendResetLink}>
              <label>
                Email address
                <input required name="email" type="email" placeholder="you@example.com" autoComplete="email" />
              </label>
              <button className={styles.authSubmit} type="submit" disabled={loading}>
                {loading ? <CircleNotch className={styles.spinner} size={19} /> : "Email me a reset link"}
                {!loading && <ArrowRight size={17} />}
              </button>
              <button type="button" className={styles.authSwitch} onClick={() => { setForgotPassword(false); setNotice(null); setFormError(null); }}>
                Back to sign in
              </button>
            </form>
          ) : (<>
          <button
            className={styles.googleButton}
            type="button"
            disabled={googleLoading || loading}
            onClick={() => {
              if (!checkAgreement()) return;
              setGoogleLoading(true);
              onGoogle(mode === "new" && policiesAccepted && ageConfirmed);
            }}
          >
            {googleLoading ? (
              <CircleNotch className={styles.spinner} size={20} />
            ) : (
              <GoogleLogo size={20} weight="bold" />
            )}
            {googleLoading ? "Opening Google…" : "Continue with Google"}
          </button>
          <div className={styles.divider}>
            <span>OR CONTINUE WITH EMAIL</span>
          </div>

          <form id="kaizen-auth-form" onSubmit={submit} noValidate={mode === "new" && !(policiesAccepted && ageConfirmed)} className={styles.authForm}>
            {mode === "new" && (
              <label>
                Full name
                <input
                  required
                  name="name"
                  placeholder="Your full name"
                  autoComplete="name"
                />
              </label>
            )}
            <label>
              Email address
              <input
                required
                name="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
              />
            </label>
            <label>
              Password
              <div className={styles.passwordField}>
                <input
                  required
                  name="password"
                  type={passwordVisible ? "text" : "password"}
                  minLength={8}
                  placeholder="At least 8 characters"
                  autoComplete={
                    mode === "new" ? "new-password" : "current-password"
                  }
                />
                <button
                  className={styles.passwordToggle}
                  type="button"
                  onClick={() => setPasswordVisible((visible) => !visible)}
                  aria-label={
                    passwordVisible ? "Hide password" : "Show password"
                  }
                  aria-pressed={passwordVisible}
                >
                  {passwordVisible ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </label>
            {mode === "back" && (
              <button type="button" className={styles.authSwitch} onClick={() => { setForgotPassword(true); setFormError(null); setNotice(null); }}>
                Forgot password?
              </button>
            )}
            {mode === "new" && (
              <label className={styles.policyAgreement}>
                <input
                  type="checkbox"
                  name="policies_accepted"
                  required
                  checked={policiesAccepted}
                  onChange={(event) => {
                    setPoliciesAccepted(event.target.checked);
                    setAgreementToast(0);
                  }}
                />
                <span>Agree to <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a></span>
              </label>
            )}
            {mode === "new" && (
              <label className={styles.policyAgreement}>
                <input
                  type="checkbox"
                  name="age_confirmed"
                  required
                  checked={ageConfirmed}
                  onChange={(event) => {
                    setAgeConfirmed(event.target.checked);
                    setAgreementToast(0);
                  }}
                />
                <span>I am 18 or older</span>
              </label>
            )}
            <button
              className={styles.authSubmit}
              type="submit"
              disabled={loading || googleLoading}
            >
              {loading ? (
                <CircleNotch className={styles.spinner} size={19} />
              ) : mode === "new" ? (
                "Create account"
              ) : (
                "Sign in"
              )}
              {!loading && <ArrowRight size={17} />}
            </button>
          </form>
          </>)}
          {mode === "back" && <p className={styles.authPolicies}><a href="/privacy">Privacy Policy</a> · <a href="/terms">Terms</a></p>}
          <button
            className={styles.authSwitch}
            type="button"
            onClick={switchMode}
          >
            {mode === "new"
              ? "Already have an account? Sign in"
              : "New to Kaizen? Create an account"}
          </button>
          <button
            className={styles.authSwitch}
            type="button"
            onClick={() => onVerificationRecovery()}
          >
            Sign in with an email code
          </button>
          <p className={styles.securityNote}>
            <ShieldCheck size={16} /> Sessions stay signed in securely until you
            explicitly log out.
          </p>
        </div>
      </div>
      {agreementToast > 0 && (
        <div className={styles.toastStack}>
          <article className={`${styles.toast} ${styles.authToast}`} role="alert" key={agreementToast}>
            <WarningCircle size={22} aria-hidden="true" />
            <div>
              <strong>Confirmation required</strong>
              <p>Agree to the Privacy Policy and Terms and confirm you are 18 or older before creating an account.</p>
            </div>
            <button type="button" aria-label="Dismiss agreement notification" onClick={() => setAgreementToast(0)}>
              <X size={18} />
            </button>
          </article>
        </div>
      )}
    </main>
  );
}

function countdownLabel(seconds: number): string {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60);
  return `${minutes}:${String(safeSeconds % 60).padStart(2, "0")}`;
}

function VerificationPanel({
  delivery,
  initialEmail,
  onDelivery,
  onVerified,
  onBack,
}: {
  delivery: PendingEmailVerification | null;
  initialEmail: string;
  onDelivery: (delivery: EmailVerificationDelivery) => void;
  onVerified: () => void;
  onBack: () => void;
}) {
  const isSignInFlow = !delivery || delivery.purpose === "sign_in";
  const [email, setEmail] = useState(initialEmail || delivery?.email || "");
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);
  const codeInputs = useRef<Array<HTMLInputElement | null>>([]);
  const submissionInProgress = useRef(false);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    setEmail(initialEmail || delivery?.email || "");
  }, [delivery?.email, initialEmail]);

  useEffect(() => {
    setDigits(["", "", "", "", "", ""]);
    setError(null);
    setChangingEmail(false);
    codeInputs.current[0]?.focus();
  }, [delivery?.verification_flow_id]);

  const expiresIn = delivery
    ? Math.max(0, Math.ceil((delivery.expiresAt - now) / 1_000))
    : 0;
  const resendIn = delivery
    ? Math.max(0, Math.ceil((delivery.resendAvailableAt - now) / 1_000))
    : 0;

  const submitCode = async (code = digits.join("")) => {
    if (!delivery || code.length !== 6 || submissionInProgress.current) return;
    submissionInProgress.current = true;
    setLoading(true);
    setError(null);
    try {
      await verifyEmailCode(delivery.verification_flow_id, code);
      setVerified(true);
      window.setTimeout(onVerified, 900);
    } catch (verificationError) {
      setDigits(["", "", "", "", "", ""]);
      setError(
        verificationError instanceof ApiError
          ? verificationError.message
          : "The verification code is invalid or has expired",
      );
      codeInputs.current[0]?.focus();
    } finally {
      submissionInProgress.current = false;
      setLoading(false);
    }
  };

  const applyDigits = (startIndex: number, value: string) => {
    const entered = value.replace(/\D/g, "").slice(0, 6 - startIndex);
    const next = [...digits];
    if (!entered) {
      next[startIndex] = "";
      setDigits(next);
      return;
    }
    entered.split("").forEach((digit, offset) => {
      next[startIndex + offset] = digit;
    });
    setDigits(next);
    setError(null);
    const nextIndex = Math.min(startIndex + entered.length, 5);
    codeInputs.current[nextIndex]?.focus();
    if (next.every(Boolean)) void submitCode(next.join(""));
  };

  const requestCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setResending(true);
    setError(null);
    try {
      const nextDelivery = delivery
        ? await changeEmailVerificationAddress(
            delivery.verification_flow_id,
            email,
          )
        : await requestEmailVerificationCode(email, "sign_in");
      onDelivery(nextDelivery);
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.message
          : "Kaizen could not send a verification code. Please try again.",
      );
    } finally {
      setResending(false);
    }
  };

  const resend = async () => {
    if (!delivery || resendIn > 0 || resending) return;
    setResending(true);
    setError(null);
    try {
      onDelivery(
        await requestEmailVerificationCode(
          delivery.email,
          delivery.purpose,
          delivery.verification_flow_id,
        ),
      );
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.message
          : "Kaizen could not resend the code. Please try again.",
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <main className={`${styles.authScreen} ${styles.authDark}`}>
      <div className={styles.authEditorial}>
        <button className={styles.authBrandButton} onClick={onBack}>
          <Brand />
        </button>
        <div>
          <p className={styles.kicker}>ONE LAST STEP.</p>
          <h2>
            {isSignInFlow
              ? "Use your email to sign in."
              : "Make this account yours."}
          </h2>
        </div>
        <div className={styles.authQuote}>
          <ShieldCheck size={34} weight="thin" />
          <p>Your code stays out of URLs and expires after ten minutes.</p>
        </div>
      </div>
      <div className={styles.authFormPanel}>
        {!verified && (
          <button
            className={styles.closeButton}
            type="button"
            onClick={onBack}
            aria-label="Return to sign in"
          >
            <X size={20} />
          </button>
        )}
        <div className={styles.authFormInner}>
          {verified ? (
            <div className={styles.verificationSuccess} role="status">
              <CheckCircle size={46} weight="thin" />
              <p className={styles.kicker}>
                {isSignInFlow ? "SIGNED IN" : "EMAIL VERIFIED"}
              </p>
              <h1>{isSignInFlow ? "Sign-in confirmed." : "Email verified."}</h1>
              <p>Opening your Kaizen workspace…</p>
            </div>
          ) : delivery && !changingEmail ? (
            <>
              <p className={styles.kicker}>CHECK YOUR EMAIL</p>
              <h1>Enter your six-digit code.</h1>
              <p className={styles.authDescription}>
                We sent a {isSignInFlow ? "sign-in" : "verification"} code to{" "}
                <strong>{delivery.email}</strong>. Return here after copying
                the code from your email.
              </p>
              {error && (
                <div className={styles.authError} role="alert">
                  <WarningCircle size={19} />
                  <span>{error}</span>
                </div>
              )}
              <form
                className={styles.verificationForm}
                onSubmit={(event) => {
                  event.preventDefault();
                  void submitCode();
                }}
              >
                <div
                  className={styles.codeInputs}
                  onPaste={(event) => {
                    event.preventDefault();
                    applyDigits(0, event.clipboardData.getData("text"));
                  }}
                >
                  {digits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(element) => {
                        codeInputs.current[index] = element;
                      }}
                      value={digit}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      maxLength={index === 0 ? 6 : 1}
                      aria-label={`Verification code digit ${index + 1}`}
                      disabled={loading || expiresIn === 0}
                      onChange={(event) => applyDigits(index, event.target.value)}
                      onKeyDown={(event) => {
                        if (
                          event.key === "Backspace" &&
                          !digits[index] &&
                          index > 0
                        ) {
                          codeInputs.current[index - 1]?.focus();
                        }
                      }}
                      onFocus={(event) => event.currentTarget.select()}
                    />
                  ))}
                </div>
                <div className={styles.verificationMeta}>
                  <span>
                    {expiresIn > 0
                      ? `Code expires in ${countdownLabel(expiresIn)}`
                      : "This code has expired"}
                  </span>
                  <button
                    type="button"
                    disabled={resendIn > 0 || resending}
                    onClick={() => void resend()}
                  >
                    {resending
                      ? "Sending…"
                      : resendIn > 0
                        ? `Resend in ${resendIn}s`
                        : "Resend code"}
                  </button>
                </div>
                <button
                  className={styles.authSubmit}
                  type="submit"
                  disabled={loading || expiresIn === 0 || !digits.every(Boolean)}
                >
                  {loading ? (
                    <CircleNotch className={styles.spinner} size={19} />
                  ) : (
                    isSignInFlow ? "Sign in and continue" : "Verify and continue"
                  )}
                  {!loading && <ArrowRight size={17} />}
                </button>
              </form>
              {!isSignInFlow && (
                <button
                  className={styles.authSwitch}
                  type="button"
                  onClick={() => {
                    setEmail(delivery.email);
                    setError(null);
                    setChangingEmail(true);
                  }}
                >
                  Change email address
                </button>
              )}
              <p className={styles.securityNote}>
                <ShieldCheck size={16} /> Five incorrect attempts are allowed.
              </p>
            </>
          ) : (
            <>
              <p className={styles.kicker}>
                {delivery ? "VERIFY YOUR ACCOUNT" : "EMAIL CODE SIGN-IN"}
              </p>
              <h1>{delivery ? "Correct your email." : "Send a sign-in code."}</h1>
              <p className={styles.authDescription}>
                {delivery
                  ? "Enter the correct address. Kaizen will update your pending account and send a new code."
                  : "Enter the email used for your account. If it matches an account, Kaizen will send a one-time sign-in code."}
              </p>
              {error && (
                <div className={styles.authError} role="alert">
                  <WarningCircle size={19} />
                  <span>{error}</span>
                </div>
              )}
              <form className={styles.authForm} onSubmit={requestCode}>
                <label>
                  Email address
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    placeholder="you@example.com"
                  />
                </label>
                <button
                  className={styles.authSubmit}
                  type="submit"
                  disabled={resending}
                >
                  {resending ? (
                    <CircleNotch className={styles.spinner} size={19} />
                  ) : (
                    delivery
                      ? "Update email and send code"
                      : "Send sign-in code"
                  )}
                  {!resending && <ArrowRight size={17} />}
                </button>
              </form>
              <button
                className={styles.authSwitch}
                type="button"
                onClick={() => {
                  if (delivery) {
                    setChangingEmail(false);
                    setEmail(delivery.email);
                    setError(null);
                  } else {
                    onBack();
                  }
                }}
              >
                {delivery ? "Keep current email" : "Back to sign in"}
              </button>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

type AuthEntry = "signup" | "login";
const AUTH_PATHS: Record<AuthMode, string> = { new: "/signup", back: "/login" };

/** Keep the address bar in step with the screen without reloading. */
function showPath(path: string, mode: "push" | "replace" = "replace") {
  const url = new URL(window.location.href);
  if (url.pathname === path) return;
  url.pathname = path;
  window.history[mode === "push" ? "pushState" : "replaceState"]({}, "", url);
}

/**
 * `entry` is set by the /signup and /login routes, which open straight onto
 * the auth screen (also in the server-rendered HTML).
 */
export function KaizenApp({ entry: entryRoute }: { entry?: AuthEntry } = {}) {
  const [screen, setScreen] = useState<Screen>(entryRoute ? "auth" : "landing");
  const [authMode, setAuthMode] = useState<AuthMode>(entryRoute === "login" ? "back" : "new");
  const [authError, setAuthError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const authChosenByUser = useRef(false);
  const [verification, setVerification] =
    useState<PendingEmailVerification | null>(null);
  const [verificationEmail, setVerificationEmail] = useState("");

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const authResult = currentUrl.searchParams.get("auth");
    const oauthError = currentUrl.searchParams.get("auth_error");
    const invitationCode = currentUrl.searchParams.get("invitation_code");
    const entry = entryRoute ?? currentUrl.searchParams.get("entry");
    currentUrl.searchParams.delete("entry");
    currentUrl.searchParams.delete("auth");
    currentUrl.searchParams.delete("auth_error");
    currentUrl.searchParams.delete("invitation_code");
    window.history.replaceState({}, "", currentUrl);

    if (invitationCode) window.localStorage.setItem("kaizen_invitation_code", invitationCode);

    if (oauthError) {
      setAuthMode(oauthError === "agreement_required" ? "new" : "back");
      setAuthError(
        oauthError === "agreement_required" ? "Agree to the Privacy Policy and Terms, confirm you are 18 or older, then continue with Google to create your account." : oauthErrorMessages[oauthError] ??
          "Google sign-in could not be completed. Please try again.",
      );
      setScreen("auth");
      setSessionReady(true);
      return;
    }

    void restoreSession()
      .then((token) => {
        if (authChosenByUser.current) return;
        if (token) {
          writePendingVerification(null);
          setScreen("app");
          return;
        }
        const pending = readPendingVerification();
        if (pending) {
          setVerification(pending);
          setVerificationEmail(pending.email);
          setScreen("verification");
          return;
        }
        if (invitationCode) {
          setAuthMode("back");
          setAuthError("Sign in or create the invited email account to accept this private group invitation.");
          setScreen("auth");
          return;
        }
        if (authResult === "google") {
          setAuthMode("back");
          setAuthError(
            "Google verified you, but Kaizen could not restore the new session.",
          );
          setScreen("auth");
          return;
        }
        if (entry === "signup" || entry === "login") {
          setAuthMode(entry === "signup" ? "new" : "back");
          setScreen("auth");
        }
      })
      .finally(() => setSessionReady(true));
  }, [entryRoute]);

  // Signed-in, verifying and landing screens live at "/"; auth has its own URL.
  useEffect(() => {
    if (!sessionReady && !entryRoute) return;
    showPath(screen === "auth" ? AUTH_PATHS[authMode] : "/");
    document.title = screen !== "auth" ? SITE_TITLE
      : authMode === "new" ? "Create your account | Kaizen" : "Sign in | Kaizen";
  }, [screen, authMode, sessionReady, entryRoute]);

  // Back/forward between the landing page and the auth screen.
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname;
      if (path === "/signup" || path === "/login") {
        setAuthMode(path === "/signup" ? "new" : "back");
        setScreen((current) => current === "app" ? current : "auth");
      } else if (path === "/") {
        setScreen((current) => current === "auth" ? "landing" : current);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const openAuth = (mode: AuthMode) => {
    authChosenByUser.current = true;
    // Back returns to the landing page.
    showPath(AUTH_PATHS[mode], "push");
    setSessionReady(true);
    setAuthMode(mode);
    setAuthError(null);
    setScreen("auth");
  };

  const changeAuthMode = (mode: AuthMode) => {
    setAuthError(null);
    setAuthMode(mode);
  };

  const storeVerification = (delivery: EmailVerificationDelivery) => {
    const pending = pendingVerification(delivery);
    setVerification(pending);
    setVerificationEmail(pending.email);
    writePendingVerification(pending);
    setAuthError(null);
    setScreen("verification");
  };

  const openVerificationRecovery = (email = "", reusePending = true) => {
    const pending = reusePending ? readPendingVerification() : null;
    if (
      pending &&
      (!email || pending.email.toLowerCase() === email.trim().toLowerCase())
    ) {
      setVerification(pending);
      setVerificationEmail(pending.email);
      setAuthError(null);
      setScreen("verification");
      return;
    }
    setVerification(null);
    setVerificationEmail(email);
    writePendingVerification(null);
    setAuthError(null);
    setScreen("verification");
  };

  const finishVerification = () => {
    setVerification(null);
    setVerificationEmail("");
    writePendingVerification(null);
    setScreen("app");
  };

  const logout = async () => {
    try {
      await logoutSession();
    } finally {
      setScreen("landing");
    }
  };

  // Render public content in the initial HTML while session restoration runs.
  // The decorative WebGL bundle is loaded only for an actual landing-page visit.
  if (!sessionReady && screen !== "auth") return <Landing ready={false} onAuth={openAuth} />;

  if (screen === "verification")
    return (
      <VerificationPanel
        delivery={verification}
        initialEmail={verificationEmail}
        onDelivery={storeVerification}
        onVerified={finishVerification}
        onBack={() => {
          setAuthMode("back");
          setAuthError(null);
          setScreen("auth");
        }}
      />
    );

  if (screen === "auth")
    return (
      <AuthPanel
        mode={authMode}
        onMode={changeAuthMode}
        authError={authError}
        onGoogle={(accepted) => window.location.assign(accepted ? `${googleLoginUrl}?policies_accepted=true&age_confirmed=true` : googleLoginUrl)}
        onClose={() => setScreen("landing")}
        onEnter={() => {
          writePendingVerification(null);
          setVerification(null);
          setScreen("app");
        }}
        onVerification={storeVerification}
        onVerificationRecovery={openVerificationRecovery}
      />
    );
  if (screen === "app") {
    const demoEnabled =
      process.env.NODE_ENV !== "production" &&
      process.env.NEXT_PUBLIC_ENABLE_DEMO_DATA === "true";
    return demoEnabled ? (
      <DemoApplication onLogout={() => void logout()} />
    ) : (
      <ProductionApplication onLogout={() => void logout()} />
    );
  }
  return <Landing onAuth={openAuth} />;
}

export default KaizenApp;
